import React, { useState, useEffect } from 'react';
import { categoriesAPI } from '../../utils/api';
import { LoadingSpinner } from '../../components/common';

const EMPTY_FORM = { name: '', slug: '', icon: '', description: '', sort_order: 0, is_active: 1 };
const EMPTY_SUB_FORM = { name: '', slug: '', sort_order: 0, is_active: 1 };

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sortCol, setSortCol] = useState('sort_order');
  const [sortDir, setSortDir] = useState('asc');

  // Subcategory state
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [subForm, setSubForm] = useState(EMPTY_SUB_FORM);
  const [editingSub, setEditingSub] = useState(null);

  const load = () => categoriesAPI.getAll().then(r => setCategories(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setSub = (k, v) => setSubForm(p => ({ ...p, [k]: v }));

  const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const openNew = () => { setForm(EMPTY_FORM); setEditing(null); setShowForm(true); setError(''); };
  const openEdit = (c) => { setForm({ ...c }); setEditing(c.id); setShowForm(true); setError(''); };

  const handleSave = async () => {
    if (!form.name || !form.slug) { setError('Name and Slug are required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) await categoriesAPI.update(editing, form);
      else await categoriesAPI.create(form);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this category?')) return;
    await categoriesAPI.delete(id);
    load();
  };

  // Subcategory Handlers
  const toggleExpand = (id) => {
    if (expandedCategory !== id) {
      setExpandedCategory(id);
      setEditingSub(null);
      setSubForm(EMPTY_SUB_FORM);
    } else {
      setExpandedCategory(null);
    }
  };

  const handleSaveSub = async (categoryId) => {
    if (!subForm.name || !subForm.slug) { alert('Name and Slug are required'); return; }
    try {
      if (editingSub) await categoriesAPI.updateSub(editingSub, subForm);
      else await categoriesAPI.createSub(categoryId, subForm);
      setSubForm(EMPTY_SUB_FORM);
      setEditingSub(null);
      load();
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditSub = (sub) => {
    setSubForm({ ...sub });
    setEditingSub(sub.id);
  };

  const handleDeleteSub = async (id) => {
    if (!window.confirm('Delete this subcategory?')) return;
    await categoriesAPI.deleteSub(id);
    load();
  };

  const toggleSort = (col) => { setSortDir(sortCol === col && sortDir === 'asc' ? 'desc' : 'asc'); setSortCol(col); };
  const si = (col) => <span style={{ fontSize: '0.65rem', marginLeft: 2, opacity: sortCol === col ? 1 : 0.3 }}>{sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>;
  const sortedCategories = [...categories].sort((a, b) => {
    const va = a[sortCol] ?? ''; const vb = b[sortCol] ?? '';
    const n = parseFloat(va), m = parseFloat(vb);
    const cmp = !isNaN(n) && !isNaN(m) ? n - m : String(va).toLowerCase().localeCompare(String(vb).toLowerCase());
    return sortDir === 'desc' ? -cmp : cmp;
  });

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div><h1>Categories</h1><p>{categories.length} categories</p></div>
        <button className="btn btn-primary" onClick={openNew}>+ Add Category</button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #2563eb' }}>
          <h2 style={{ fontWeight: 700, marginBottom: '1rem' }}>{editing ? 'Edit Category' : 'New Category'}</h2>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                className="form-input"
                value={form.name}
                onChange={e => {
                  set('name', e.target.value);
                  if (!editing) set('slug', slugify(e.target.value));
                }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Slug *</label>
              <input className="form-input" value={form.slug} onChange={e => set('slug', slugify(e.target.value))} placeholder="auto-generated" />
            </div>
            <div className="form-group">
              <label className="form-label">Icon (emoji)</label>
              <input className="form-input" value={form.icon} onChange={e => set('icon', e.target.value)} placeholder="📚" maxLength={4} />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Sort Order</label>
              <input className="form-input" type="number" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)} />
            </div>
            {editing && (
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select w-full" value={form.is_active} onChange={e => set('is_active', e.target.value)}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="form-input" value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
          <div className="flex gap-2" style={{ marginTop: '0.5rem' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Icon</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('name')}>Name {si('name')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('slug')}>Slug {si('slug')}</th>
                  <th>Description</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('sort_order')}>Sort {si('sort_order')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('is_active')}>Status {si('is_active')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedCategories.map(c => (
                  <React.Fragment key={c.id}>
                    <tr>
                      <td style={{ fontSize: '1.4rem' }}>{c.icon || '—'}</td>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#6b7280' }}>{c.slug}</td>
                      <td style={{ fontSize: '0.82rem', color: '#6b7280', maxWidth: 200 }}>{c.description || '—'}</td>
                      <td>{c.sort_order}</td>
                      <td>
                        <span className={`badge ${c.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn btn-outline btn-sm" onClick={() => toggleExpand(c.id)}>Subcats</button>
                          <button className="btn btn-outline btn-sm" onClick={() => openEdit(c)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id)}>Del</button>
                        </div>
                      </td>
                    </tr>
                    {expandedCategory === c.id && (
                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                        <td colSpan={7} style={{ padding: '1.5rem', borderBottom: '2px solid #e5e7eb' }}>
                          <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <h4 style={{ marginBottom: '1rem', fontWeight: 600, color: '#374151' }}>Subcategories for {c.name}</h4>
                              {c.subcategories?.length > 0 ? (
                                <table className="table" style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                                  <thead style={{ background: '#f9fafb' }}>
                                    <tr>
                                      <th style={{ padding: '0.75rem 1rem' }}>Name</th>
                                      <th style={{ padding: '0.75rem 1rem' }}>Slug</th>
                                      <th style={{ padding: '0.75rem 1rem' }}>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {c.subcategories.map(sub => (
                                      <tr key={sub.id}>
                                        <td style={{ padding: '0.75rem 1rem', fontWeight: 500 }}>{sub.name}</td>
                                        <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.8rem', color: '#6b7280' }}>{sub.slug}</td>
                                        <td style={{ padding: '0.75rem 1rem' }}>
                                          <div className="flex gap-2">
                                            <button className="btn btn-outline btn-sm" onClick={() => handleEditSub(sub)}>Edit</button>
                                            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteSub(sub.id)}>Del</button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>No subcategories found. Create one to the right.</p>}
                            </div>
                            
                            <div style={{ width: '320px', background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                              <h4 style={{ marginBottom: '1rem', fontWeight: 600, color: '#374151' }}>{editingSub ? 'Edit' : 'Add'} Subcategory</h4>
                              <div className="form-group" style={{ marginBottom: '1rem' }}>
                                <label className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Name *</label>
                                <input className="form-input" style={{ padding: '0.5rem' }} value={subForm.name} onChange={e => { setSub('name', e.target.value); if(!editingSub) setSub('slug', slugify(e.target.value)); }} />
                              </div>
                              <div className="form-group" style={{ marginBottom: '1rem' }}>
                                <label className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Slug *</label>
                                <input className="form-input" style={{ padding: '0.5rem' }} value={subForm.slug} onChange={e => setSub('slug', slugify(e.target.value))} />
                              </div>
                              <div className="flex gap-2" style={{ marginTop: '1.5rem' }}>
                                <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => handleSaveSub(c.id)}>Save</button>
                                {editingSub && <button className="btn btn-outline btn-sm" onClick={() => { setEditingSub(null); setSubForm(EMPTY_SUB_FORM); }}>Cancel</button>}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {categories.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>No categories yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
